import { api } from './api';
import { customerApi } from './customer-api';

/**
 * Messaging API. Same backend routes serve both sides — we pick the
 * axios instance based on which JWT the calling page is using.
 *
 * Pro pages use the `pro` variant (eats the pro JWT cookie / Authorization
 * header); customer pages use `customer`.
 */
export type MessageThread = {
  counterparty: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    profile_pic: string | null;
    profile_pic_preview?: string | null;
    user_type: string;
  };
  last_message: {
    id: string;
    body: string;
    sender_user_id: string;
    created_at: string;
    read_at: string | null;
  };
  booking_id: string | null;
  unread_count: number;
};

export type PaymentRequestData = {
  id: string;
  amount: number; // pro's rate (what they type)
  customer_amount: number | null; // all-in gross the client pays (rate + fee + VAT)
  pro_net_amount?: number; // what the pro actually nets (gross − fee − VAT)
  currency: string;
  description: string;
  status: 'PENDING' | 'PAID' | 'CANCELLED' | 'EXPIRED';
  expires_at: string;
};

export type PatchTestData = {
  id: string;
  status: 'PROPOSED' | 'CONFIRMED' | 'DECLINED' | 'CANCELLED';
  scheduled_start: string;
  duration_minutes: number;
  note: string | null;
  service_name: string | null;
  professional_name: string;
  booking_id: string | null;
};

export type ChatMessage = {
  id: string;
  body: string;
  message_type?: 'TEXT' | 'PAYMENT_REQUEST' | 'IMAGE' | 'PATCH_TEST_REQUEST';
  payment_request?: PaymentRequestData | null;
  patch_test?: PatchTestData | null;
  // Presigned read URL of the attached photo for IMAGE messages.
  attachment_url?: string | null;
  sender_user_id: string;
  recipient_user_id: string;
  booking_id: string | null;
  created_at: string;
  read_at: string | null;
};

type Variant = 'pro' | 'customer';
const client = (v: Variant) => (v === 'pro' ? api : customerApi);

export async function listMessageThreads(variant: Variant) {
  const res = await client(variant).get('/me/messages/threads');
  return res.data as { status: boolean; data: MessageThread[] };
}

export async function getMessageThread(
  variant: Variant,
  otherUserId: string,
) {
  const res = await client(variant).get(
    `/me/messages/thread/${encodeURIComponent(otherUserId)}`,
  );
  return res.data as {
    status: boolean;
    counterparty: MessageThread['counterparty'];
    data: ChatMessage[];
  };
}

export async function sendMessage(
  variant: Variant,
  payload: {
    recipient_user_id: string;
    body: string;
    booking_id?: string | null;
    message_type?: 'TEXT' | 'IMAGE';
    attachment_url?: string | null;
  },
) {
  const res = await client(variant).post('/me/messages', payload);
  return res.data as { status: boolean; id: string; message: string };
}

/**
 * Upload a chat photo (website → API → S3) and get back its URL, which is
 * then sent as an IMAGE message's `attachment_url`. Reuses the shared
 * `/uploads/me` endpoint (GENERIC purpose accepts jpeg/png/webp ≤10MB).
 */
export async function uploadMessageImage(variant: Variant, file: File) {
  const form = new FormData();
  form.append('file', file, file.name);
  form.append('purpose', 'GENERIC');
  const res = await client(variant).post('/uploads/me', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data as { status: boolean; key: string; url: string };
}

export async function markThreadRead(variant: Variant, otherUserId: string) {
  const res = await client(variant).put(
    `/me/messages/thread/${encodeURIComponent(otherUserId)}/read`,
  );
  return res.data as { status: boolean; affected: number };
}

export async function getUnreadMessageCount(variant: Variant) {
  const res = await client(variant).get('/me/messages/unread-count');
  return res.data as { status: boolean; count: number };
}

// ---- In-chat payment requests ----

// Pro creates a request (posts a PAYMENT_REQUEST bubble).
export async function createPaymentRequest(
  variant: Variant,
  payload: {
    recipient_user_id: string;
    amount: number;
    description: string;
    booking_id?: string | null;
  },
) {
  const res = await client(variant).post('/me/payment-requests', payload);
  return res.data as { status: boolean; id: string; message_id: string };
}

// Client starts paying — returns a Stripe Checkout URL to redirect to.
export async function payPaymentRequest(variant: Variant, id: string) {
  const res = await client(variant).post(
    `/me/payment-requests/${encodeURIComponent(id)}/pay`,
  );
  return res.data as { status: boolean; checkout_url: string };
}

// Pro cancels a still-unpaid request.
export async function cancelPaymentRequest(variant: Variant, id: string) {
  const res = await client(variant).post(
    `/me/payment-requests/${encodeURIComponent(id)}/cancel`,
  );
  return res.data as { status: boolean };
}

// ---- In-chat patch-test bookings ----

// Pro proposes a patch test (posts a PATCH_TEST_REQUEST bubble).
export async function proposePatchTest(
  variant: Variant,
  payload: {
    recipient_user_id: string;
    scheduled_start: string; // ISO
    duration_minutes?: number;
    booking_id?: string | null;
    service_id?: string | null;
    note?: string | null;
  },
) {
  const res = await client(variant).post('/me/patch-tests', payload);
  return res.data as { status: boolean; id: string; message_id: string };
}

// Client confirms a proposed patch test (blocks the slot on both calendars).
export async function confirmPatchTest(variant: Variant, id: string) {
  const res = await client(variant).put(
    `/me/patch-tests/${encodeURIComponent(id)}/confirm`,
  );
  return res.data as { status: boolean };
}

// Client declines a proposed patch test.
export async function declinePatchTest(variant: Variant, id: string) {
  const res = await client(variant).put(
    `/me/patch-tests/${encodeURIComponent(id)}/decline`,
  );
  return res.data as { status: boolean };
}

// Pro cancels a proposed/confirmed patch test (frees the calendar block).
export async function cancelPatchTest(variant: Variant, id: string) {
  const res = await client(variant).post(
    `/me/patch-tests/${encodeURIComponent(id)}/cancel`,
  );
  return res.data as { status: boolean };
}

// Client's upcoming confirmed patch tests (for the bookings list).
export async function listMyPatchTests(variant: Variant) {
  const res = await client(variant).get('/me/patch-tests/mine');
  return res.data as { status: boolean; data: PatchTestData[] };
}
